import { ref, onMounted, onUnmounted } from 'vue';
import { httpService } from '../../services/httpService';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';

import '../../contracts/VendasKpi_CardsResponse';
import '../../contracts/VendasKpi_EvolucaoDiariaResponse';
import '../../contracts/VendasKpi_FidelizadosResponse';
import '../../contracts/VendasKpi_RecorrenciaResponse';

import type { VendasKpi_CardsResponseDados } from '../../contracts/VendasKpi_CardsResponse';
import type { VendasKpi_EvolucaoDiariaPontoRow } from '../../contracts/VendasKpi_EvolucaoDiariaPontoRow';
import type { VendasKpi_FidelizadosMesRow } from '../../contracts/VendasKpi_FidelizadosMesRow';
import type { VendasKpi_RecorrenciaResponseDados } from '../../contracts/VendasKpi_RecorrenciaResponse';

const BASE = '/index.php/dashboards/vendas_kpi';

export function useDashboardVendasKpi() {
  const cards = ref<VendasKpi_CardsResponseDados | null>(null);
  const evolucaoDiaria = ref<VendasKpi_EvolucaoDiariaPontoRow[] | null>(null);
  const fidelizados = ref<VendasKpi_FidelizadosMesRow[] | null>(null);
  const recorrencia = ref<VendasKpi_RecorrenciaResponseDados | null>(null);

  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const fetchCards = async (): Promise<void> => {
    log('useDashboardVendasKpi', 'fetchCards', 'GET', `${BASE}/cards`);
    const response = await httpService.get(`${BASE}/cards`, {
      responseContract: 'VendasKpi_CardsResponse',
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao buscar cards de vendas');
    }
    cards.value = response.dados ?? null;
  };

  const fetchEvolucaoDiaria = async (): Promise<void> => {
    log('useDashboardVendasKpi', 'fetchEvolucaoDiaria', 'GET', `${BASE}/evolucao_diaria`);
    const response = await httpService.get(`${BASE}/evolucao_diaria`, {
      responseContract: 'VendasKpi_EvolucaoDiariaResponse',
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao buscar evolução diária');
    }
    evolucaoDiaria.value = response.dados?.pontos ?? [];
  };

  const fetchFidelizados = async (): Promise<void> => {
    log('useDashboardVendasKpi', 'fetchFidelizados', 'GET', `${BASE}/fidelizados`);
    const response = await httpService.get(`${BASE}/fidelizados`, {
      responseContract: 'VendasKpi_FidelizadosResponse',
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao buscar fidelizados');
    }
    fidelizados.value = response.dados?.meses ?? [];
  };

  const fetchRecorrencia = async (): Promise<void> => {
    log('useDashboardVendasKpi', 'fetchRecorrencia', 'GET', `${BASE}/recorrencia`);
    const response = await httpService.get(`${BASE}/recorrencia`, {
      responseContract: 'VendasKpi_RecorrenciaResponse',
    });
    if (!response.success) {
      throw new Error(response.error || 'Erro ao buscar recorrência');
    }
    recorrencia.value = response.dados ?? null;
  };

  const fetchAll = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;
    try {
      await Promise.all([
        fetchCards(),
        fetchEvolucaoDiaria(),
        fetchFidelizados(),
        fetchRecorrencia(),
      ]);
      eventBus.emit('dashboard.vendas_kpi.loaded');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      log('useDashboardVendasKpi', 'fetchAll', 'Error', message);
      error.value = message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const onFilterUpdated = (): void => {
    log('useDashboardVendasKpi', 'onFilterUpdated', 'Refetching');
    fetchAll().catch((err) => {
      log('useDashboardVendasKpi', 'onFilterUpdated', 'Refetch failed', err);
    });
  };

  onMounted(() => {
    eventBus.on('filter.updated', onFilterUpdated);
    // Fetch data on mount since filter.updated may have already fired
    // before this component was mounted (e.g., SPA navigation).
    fetchAll().catch((err) => {
      log('useDashboardVendasKpi', 'onMounted', 'Initial fetch failed', err);
    });
  });

  onUnmounted(() => {
    eventBus.off('filter.updated', onFilterUpdated);
  });

  return {
    cards,
    evolucaoDiaria,
    fidelizados,
    recorrencia,
    isLoading,
    error,
    fetchCards,
    fetchEvolucaoDiaria,
    fetchFidelizados,
    fetchRecorrencia,
    fetchAll,
  };
}
